"use client";

import {
	type CSSProperties,
	createContext,
	type HTMLAttributes,
	type ReactNode,
	type RefObject,
	useContext,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { cn } from "../../lib/cn";

const RADIUS_REM = {
	lg: "0.5rem",
	xl: "0.75rem",
	"2xl": "1rem",
	"3xl": "1.5rem",
	"4xl": "2rem",
	/** Pill / circle shells (e.g. {@link GlassCircleChrome}). */
	full: "9999px",
} as const;

export type GlassBorderRadius = keyof typeof RADIUS_REM;

/** Per-corner radius: token scale, **`"none"`** (0), or inherit default from {@link GlassBorderProps.radius}. */
export type GlassBorderCornerRadiusToken = GlassBorderRadius | "none";

export interface GlassBorderCornerRadii {
	topLeft?: GlassBorderCornerRadiusToken;
	topRight?: GlassBorderCornerRadiusToken;
	bottomRight?: GlassBorderCornerRadiusToken;
	bottomLeft?: GlassBorderCornerRadiusToken;
}

/** Per-edge glass band width in px; unset edges use {@link GlassBorderProps.borderWidth}. */
export interface GlassBorderEdgeBorderWidths {
	top?: number;
	right?: number;
	bottom?: number;
	left?: number;
}

/** Rem radius tokens (matches Tailwind `rounded-*` scale in this repo). */
export const GLASS_BORDER_RADIUS_REM: Readonly<
	Record<GlassBorderRadius, string>
> = RADIUS_REM;

/** Tailwind `border` default — subtract from outer `border-radius` so the frosted plate tracks the shell stroke. */
export const GLASS_SHELL_BORDER_WIDTH_PX = 1;
const GLASS_SHELL_BORDER_CSS = `${GLASS_SHELL_BORDER_WIDTH_PX}px`;

/** Padding halo vs absolutely positioned halo (unchanged). */
export type GlassBorderVariant = "inner" | "outer";

/**
 * Frosted band appearance relative to page background:
 * - **`system`**: light mode → dark translucent rim (for light backgrounds); dark mode → light translucent (for dark backgrounds).
 * - **`tone="dark"`**: always dark translucent (same as system in light mode).
 * - **`tone="light"`**: light frosted plate on dark bands; shell uses a subtle 6% white hairline (mirrors dark tone / system 6% mix).
 */
export type GlassBorderTone = "light" | "dark" | "system";

/** CSS variable set on the inner clip node — use for `border-radius` / `BorderBeam` alignment. */
export const GLASS_BORDER_INNER_RADIUS_VAR = "--glass-inner-radius";

/** Resolved length for Tailwind arbitrary values, e.g. `rounded-[length:var(--glass-inner-radius)]`. */
export const GLASS_BORDER_INNER_RADIUS_CSS = "var(--glass-inner-radius)";

/** Default {@link GlassBorderProps.borderWidth} in px. */
export const GLASS_BORDER_DEFAULT_BORDER_WIDTH_PX = 6;

/** Explicit light: white translucent glass (both color schemes) — for dark page bands. */
export const GLASS_BORDER_TONE_LIGHT_PLATE_CLASS =
	"bg-white/20 shadow-xs dark:bg-white/20";

/** Subtle light hairline on dark bands — 6% mix, parallel to {@link GLASS_BORDER_TONE_DARK_SHELL_BORDER_CLASS}. */
export const GLASS_BORDER_TONE_LIGHT_SHELL_BORDER_COLOR_CLASS =
	"border-[color-mix(in_srgb,white_6%,transparent)] dark:border-[color-mix(in_srgb,white_6%,transparent)]";

/** Opaque 1px rim for {@link GlassBorderTone} `light` (dark page bands). */
export const GLASS_BORDER_TONE_LIGHT_SHELL_BORDER_CLASS = cn(
	"border",
	GLASS_BORDER_TONE_LIGHT_SHELL_BORDER_COLOR_CLASS,
);

/** Explicit dark: black translucent glass (both color schemes) — for light page bands. */
export const GLASS_BORDER_TONE_DARK_PLATE_CLASS =
	"bg-black/6 shadow-xs dark:bg-black/6";

export const GLASS_BORDER_TONE_DARK_SHELL_BORDER_CLASS =
	"border border-[color-mix(in_srgb,black_6%,white)] dark:border-[color-mix(in_srgb,black_6%,white)]";

/** System: dark translucent in light mode, light translucent in dark mode. */
export const GLASS_BORDER_TONE_SYSTEM_PLATE_CLASS = cn(
	"bg-black/6 shadow-xs",
	"dark:bg-white/20 dark:shadow-xs",
);

export const GLASS_BORDER_TONE_SYSTEM_SHELL_BORDER_CLASS = cn(
	"border border-[color-mix(in_srgb,black_6%,white)]",
	"dark:border-[color-mix(in_srgb,white_20%,black)]",
);

/**
 * @deprecated Kept for imports; maps to {@link GLASS_BORDER_TONE_SYSTEM_PLATE_CLASS}.
 */
export const GLASS_BORDER_SURFACE_CLASS = GLASS_BORDER_TONE_SYSTEM_PLATE_CLASS;

function glassTonePlateClass(tone: GlassBorderTone): string {
	switch (tone) {
		case "light":
			return GLASS_BORDER_TONE_LIGHT_PLATE_CLASS;
		case "dark":
			return GLASS_BORDER_TONE_DARK_PLATE_CLASS;
		default:
			return GLASS_BORDER_TONE_SYSTEM_PLATE_CLASS;
	}
}

function glassToneShellBorderClass(tone: GlassBorderTone): string {
	switch (tone) {
		case "light":
			return GLASS_BORDER_TONE_LIGHT_SHELL_BORDER_CLASS;
		case "dark":
			return GLASS_BORDER_TONE_DARK_SHELL_BORDER_CLASS;
		default:
			return GLASS_BORDER_TONE_SYSTEM_SHELL_BORDER_CLASS;
	}
}

/** Border-color utilities for `variant="outer"` + asymmetric shell (widths set inline). */
function glassToneOuterAsymmetricShellBorderColorClass(
	tone: GlassBorderTone,
): string {
	switch (tone) {
		case "light":
			return GLASS_BORDER_TONE_LIGHT_SHELL_BORDER_COLOR_CLASS;
		case "dark":
			return "border-[color-mix(in_srgb,black_6%,white)] dark:border-[color-mix(in_srgb,black_6%,white)]";
		default:
			return cn(
				"border-[color-mix(in_srgb,black_6%,white)]",
				"dark:border-[color-mix(in_srgb,white_20%,black)]",
			);
	}
}

function outerAsymmetricShellBorderStyle(
	topW: number,
	rightW: number,
	bottomW: number,
	leftW: number,
): CSSProperties {
	// Longhand width/style only — a `borderTop: "Npx solid"` shorthand would
	// reset border-*-color to `currentcolor` and paint a dark rim over the glass.
	return {
		borderTopWidth: topW === 0 ? 0 : topW,
		borderTopStyle: topW === 0 ? "none" : "solid",
		borderRightWidth: rightW === 0 ? 0 : rightW,
		borderRightStyle: rightW === 0 ? "none" : "solid",
		borderBottomWidth: bottomW === 0 ? 0 : bottomW,
		borderBottomStyle: bottomW === 0 ? "none" : "solid",
		borderLeftWidth: leftW === 0 ? 0 : leftW,
		borderLeftStyle: leftW === 0 ? "none" : "solid",
	};
}

/** Bleed (px) when asymmetric outer plate extends past a zero-width **top** / **left** edge. */
const ZERO_EDGE_PLATE_BLEED_PX = 3;

function asymmetricPlateInsetPx(
	edge: "top" | "right" | "bottom" | "left",
	edgeWidthPx: number,
): number {
	if (edgeWidthPx !== 0) {
		return -edgeWidthPx;
	}
	if (edge === "bottom" || edge === "right") {
		return 0;
	}
	return -ZERO_EDGE_PLATE_BLEED_PX;
}

export interface GlassBorderContextValue {
	/** Resolved top-left radius (px) of the inner clip, for `BorderBeam` path alignment. */
	innerCornerRadiusPx: number;
	innerClipRef: RefObject<HTMLDivElement | null>;
	/**
	 * When `false`, panels must not set `border-radius` — Safari still clips
	 * overflow:visible descendants when radius is present (HUD avatar / bag).
	 */
	clipContent: boolean;
}

const GlassBorderContext = createContext<GlassBorderContextValue | null>(null);

export function useGlassBorder(): GlassBorderContextValue | null {
	return useContext(GlassBorderContext);
}

const DEFAULT_INNER_CORNER_RADIUS_PX = 12;

export interface GlassBorderPanelProps extends HTMLAttributes<HTMLElement> {
	as?: "div" | "span" | "section" | "figure" | "article" | "nav";
	style?: CSSProperties;
	children?: ReactNode;
}

/**
 * Inner surface that shares the glass inner corner radius (`--glass-inner-radius`).
 * Renders a single element (default `div`). Inside {@link GlassBorder} `variant="inner"`, corner
 * radius follows `--glass-inner-radius`. Next to {@link GlassBorder} `variant="outer"`, there is
 * no glass context — pass matching `rounded-*` / `style.borderRadius` explicitly.
 * For **`variant="outer"`** with `cornerRadii` / `edgeBorderWidths`, the shell stroke is drawn on
 * the outer content wrapper — avoid duplicating a full `border` on this panel unless intentional.
 */
export function GlassBorderPanel({
	as = "div",
	className,
	style,
	children,
	...rest
}: GlassBorderPanelProps) {
	const Comp = as;
	const ctx = useContext(GlassBorderContext);
	const mergedStyle: CSSProperties = ctx?.clipContent
		? {
				...style,
				borderRadius: GLASS_BORDER_INNER_RADIUS_CSS,
			}
		: { ...style };

	return (
		<Comp
			className={cn("relative isolate min-h-0", className)}
			style={mergedStyle}
			{...rest}
		>
			{children}
		</Comp>
	);
}

export interface GlassBorderProps {
	children: ReactNode;
	variant?: GlassBorderVariant;
	tone?: GlassBorderTone;
	borderWidth?: number;
	radius?: GlassBorderRadius;
	className?: string;
	wrapperClassName?: string;
	contentClassName?: string;
	/** Merged onto the root wrapper (after glass CSS variables). */
	style?: CSSProperties;
	/**
	 * Stronger frosted-plate backdrop blur (and slight saturation lift).
	 * When `false`, uses a lighter blur so the plate reads flatter.
	 * @default true
	 */
	largeBlur?: boolean;
	/**
	 * When `false`, omit `backdrop-filter` on the plate. Required under transformed
	 * ancestors (e.g. Swiper `effect="cards"`) — blur layers get promoted above
	 * `z-10` content and hide callout/note text.
	 * @default true
	 */
	enableBackdropFilter?: boolean;
	/**
	 * **`fill`**: frosted plate covers the full inner area (default).
	 * **`rim`**: mask the plate to the border band only (same geometry as
	 * {@link GlassBorderRing}) so a mis-ordered plate cannot cover content.
	 * @default "fill"
	 */
	plateCoverage?: "fill" | "rim";
	/**
	 * Per-corner content `border-radius` (outer clip). Unset corners use {@link GlassBorderProps.radius}.
	 * **`variant="outer"`** only — ignored for `inner` (uniform ring math).
	 */
	cornerRadii?: GlassBorderCornerRadii;
	/**
	 * Per-edge frosted band thickness in px. Unset edges use {@link GlassBorderProps.borderWidth}.
	 * **`variant="outer"`** only — ignored for `inner`.
	 * When `cornerRadii` or `edgeBorderWidths` triggers the asymmetric outer shell, matching
	 * **content** edges use `border-*: none` for sides with width `0` (no stroke / seam).
	 * Zero-width **top** / **left** edges bleed the frosted plate; **bottom** / **right** stay
	 * flush at `0` so stacked halves do not double-blur the same strip (prefer one
	 * {@link GlassBorder} when possible).
	 */
	edgeBorderWidths?: GlassBorderEdgeBorderWidths;
	/** Overrides {@link GlassBorderProps.tone} plate fill (frosted backdrop layer). */
	plateClassName?: string;
	/** Overrides {@link GlassBorderProps.tone} shell stroke on the inner variant wrapper. */
	shellBorderClassName?: string;
	/**
	 * When `false`, content may paint outside the rounded clip (e.g. header HUD
	 * bag/avatar art that sits slightly proud of the pill). @default true
	 */
	clipContent?: boolean;
}

function resolveCornerRadiusLength(
	token: GlassBorderCornerRadiusToken | undefined,
	baseRem: string,
): string {
	if (token === "none") {
		return "0";
	}
	if (token === undefined) {
		return baseRem;
	}
	return RADIUS_REM[token];
}

function plateOuterCornerRadius(
	contentCorner: string,
	adjacentW1Px: number,
	adjacentW2Px: number,
): string {
	if (contentCorner === "0") {
		return "0";
	}
	const w = Math.max(adjacentW1Px, adjacentW2Px, 0);
	if (w === 0) {
		return contentCorner;
	}
	return `calc(${contentCorner} + ${w}px)`;
}

function parseRadiusPx(computedTopLeft: string): number {
	if (!computedTopLeft || computedTopLeft === "0px") {
		return DEFAULT_INNER_CORNER_RADIUS_PX;
	}
	const first = computedTopLeft.split(/\s+/)[0] ?? computedTopLeft;
	const n = Number.parseFloat(first);
	return Number.isFinite(n) && n > 0 ? n : DEFAULT_INNER_CORNER_RADIUS_PX;
}

/**
 * Frosted glass ring / outline used on the marketing landing page.
 *
 * Inner clip exposes `--glass-inner-radius` and context for {@link GlassBorderPanel} /
 * {@link BorderBeam} `pathCornerRadiusFromGlass`.
 */
export const GLASS_PLATE_LARGE_BLUR_CLASS =
	"backdrop-blur-2xl backdrop-saturate-150 motion-reduce:backdrop-blur-md motion-reduce:backdrop-saturate-100";
export const GLASS_PLATE_SUBTLE_BLUR_CLASS =
	"backdrop-blur-md backdrop-saturate-125 motion-reduce:backdrop-blur-sm";

export function GlassBorder({
	children,
	variant = "inner",
	tone = "system",
	borderWidth = GLASS_BORDER_DEFAULT_BORDER_WIDTH_PX,
	radius = "xl",
	className,
	wrapperClassName,
	contentClassName,
	style: rootStyleProp,
	largeBlur = true,
	enableBackdropFilter = true,
	plateCoverage = "fill",
	cornerRadii,
	edgeBorderWidths,
	plateClassName,
	shellBorderClassName,
	clipContent = true,
}: GlassBorderProps) {
	const r = RADIUS_REM[radius];
	const bw = `${borderWidth}px`;
	const cssVars = {
		"--glass-border-r": r,
		"--glass-border-w": bw,
		"--glass-shell-bw": GLASS_SHELL_BORDER_CSS,
	} as CSSProperties;

	const outerAsymmetric =
		variant === "outer" &&
		(cornerRadii !== undefined || edgeBorderWidths !== undefined);

	const topW = outerAsymmetric
		? (edgeBorderWidths?.top ?? borderWidth)
		: borderWidth;
	const rightW = outerAsymmetric
		? (edgeBorderWidths?.right ?? borderWidth)
		: borderWidth;
	const bottomW = outerAsymmetric
		? (edgeBorderWidths?.bottom ?? borderWidth)
		: borderWidth;
	const leftW = outerAsymmetric
		? (edgeBorderWidths?.left ?? borderWidth)
		: borderWidth;

	const contentRTL = outerAsymmetric
		? resolveCornerRadiusLength(cornerRadii?.topLeft, r)
		: r;
	const contentRTR = outerAsymmetric
		? resolveCornerRadiusLength(cornerRadii?.topRight, r)
		: r;
	const contentRBR = outerAsymmetric
		? resolveCornerRadiusLength(cornerRadii?.bottomRight, r)
		: r;
	const contentRBL = outerAsymmetric
		? resolveCornerRadiusLength(cornerRadii?.bottomLeft, r)
		: r;

	const plateRTL = outerAsymmetric
		? plateOuterCornerRadius(contentRTL, topW, leftW)
		: "";
	const plateRTR = outerAsymmetric
		? plateOuterCornerRadius(contentRTR, topW, rightW)
		: "";
	const plateRBR = outerAsymmetric
		? plateOuterCornerRadius(contentRBR, bottomW, rightW)
		: "";
	const plateRBL = outerAsymmetric
		? plateOuterCornerRadius(contentRBL, bottomW, leftW)
		: "";

	const innerClipRef = useRef<HTMLDivElement | null>(null);
	const [innerCornerRadiusPx, setInnerCornerRadiusPx] = useState(
		DEFAULT_INNER_CORNER_RADIUS_PX,
	);

	/** Inner clip + plate follow the shell border’s inner curve, then the glass band width. */
	const innerRadiusCalc =
		"calc(var(--glass-border-r) - var(--glass-shell-bw) - var(--glass-border-w))" as const;

	const plateRadiusInnerVariant =
		"calc(var(--glass-border-r) - var(--glass-shell-bw))" as const;

	useLayoutEffect(() => {
		if (variant !== "inner") {
			return;
		}
		const el = innerClipRef.current;
		if (!el) {
			return;
		}
		const measure = () => {
			const cs = getComputedStyle(el);
			setInnerCornerRadiusPx(parseRadiusPx(cs.borderTopLeftRadius));
		};
		measure();
		const ro = new ResizeObserver(measure);
		ro.observe(el);
		return () => {
			ro.disconnect();
		};
	}, [variant, radius, borderWidth, r, bw]);

	const contextValue = useMemo<GlassBorderContextValue>(
		() => ({
			innerCornerRadiusPx,
			innerClipRef,
			clipContent,
		}),
		[innerCornerRadiusPx, clipContent],
	);

	const plateClass = plateClassName ?? glassTonePlateClass(tone);
	const innerShellBorderClass =
		shellBorderClassName ?? glassToneShellBorderClass(tone);

	const glassPlate = (
		<div
			aria-hidden
			className={cn(
				"pointer-events-none absolute z-0",
				plateClass,
				enableBackdropFilter
					? largeBlur
						? GLASS_PLATE_LARGE_BLUR_CLASS
						: GLASS_PLATE_SUBTLE_BLUR_CLASS
					: null,
			)}
			style={{
				...cssVars,
				...(variant === "outer"
					? outerAsymmetric
						? {
								top: `${asymmetricPlateInsetPx("top", topW)}px`,
								right: `${asymmetricPlateInsetPx("right", rightW)}px`,
								bottom: `${asymmetricPlateInsetPx("bottom", bottomW)}px`,
								left: `${asymmetricPlateInsetPx("left", leftW)}px`,
								borderTopLeftRadius: plateRTL,
								borderTopRightRadius: plateRTR,
								borderBottomRightRadius: plateRBR,
								borderBottomLeftRadius: plateRBL,
							}
						: {
								inset: `calc(-1 * ${bw})`,
								borderRadius: `calc(var(--glass-border-r) + ${bw})`,
							}
					: {
							inset: 0,
							borderRadius: plateRadiusInnerVariant,
							...(plateCoverage === "rim"
								? {
										boxSizing: "border-box" as const,
										padding: bw,
										WebkitMask:
											"linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
										WebkitMaskComposite: "xor",
										mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
										maskComposite: "exclude",
									}
								: {}),
						}),
			}}
		/>
	);

	if (variant === "outer") {
		const outerRootStyle = {
			...cssVars,
			...(rootStyleProp ?? {}),
		} as CSSProperties;

		const contentRadiusStyle = outerAsymmetric
			? {
					borderTopLeftRadius: contentRTL,
					borderTopRightRadius: contentRTR,
					borderBottomRightRadius: contentRBR,
					borderBottomLeftRadius: contentRBL,
				}
			: {
					borderRadius: "var(--glass-border-r)",
				};

		const contentShellStyle = outerAsymmetric
			? ({
					...contentRadiusStyle,
					...outerAsymmetricShellBorderStyle(
						topW,
						rightW,
						bottomW,
						leftW,
					),
				} as CSSProperties)
			: (contentRadiusStyle as CSSProperties);

		return (
			<div
				className={cn(
					"relative isolate overflow-visible",
					wrapperClassName,
					className,
				)}
				style={outerRootStyle}
			>
				{glassPlate}
				<div
					className={cn(
						"relative z-10 isolate min-h-0 overflow-hidden",
						outerAsymmetric && "box-border border-solid",
						outerAsymmetric &&
							glassToneOuterAsymmetricShellBorderColorClass(tone),
						contentClassName,
					)}
					style={contentShellStyle}
				>
					{children}
				</div>
			</div>
		);
	}

	const innerClipStyle = {
		...cssVars,
		[GLASS_BORDER_INNER_RADIUS_VAR]: innerRadiusCalc,
		// Safari clips transformed “proud” children when border-radius is set even
		// with overflow:visible — keep radius only when we intentionally clip.
		...(clipContent ? { borderRadius: innerRadiusCalc } : {}),
		overflow: clipContent ? "hidden" : "visible",
		isolation: "isolate",
	} as CSSProperties;

	const innerRootStyle = {
		...cssVars,
		...(rootStyleProp ?? {}),
		padding: bw,
		// Same Safari rule as the content shell: radius + overflow:visible still
		// clips proud children (HUD avatar). Only radius when intentionally clipping.
		...(clipContent ? { borderRadius: "var(--glass-border-r)" } : {}),
	} as CSSProperties;

	return (
		<div
			className={cn(
				"relative isolate box-border",
				clipContent ? "overflow-hidden" : "overflow-visible",
				innerShellBorderClass,
				wrapperClassName,
				className,
			)}
			style={innerRootStyle}
		>
			{glassPlate}
			<GlassBorderContext.Provider value={contextValue}>
				<div
					ref={innerClipRef}
					className={cn("relative z-10 min-h-0", contentClassName)}
					style={innerClipStyle}
				>
					{children}
				</div>
			</GlassBorderContext.Provider>
		</div>
	);
}
